import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { compact, find, findIndex, map, pullAt, reject } from 'lodash'
import { produce } from 'immer'
import routes from '../routes/routes'
import itemService from '../services/item.service'
import itemApi, { ItemData } from '../apis/item.api'
import shipOrderApi, {
  ShipOrderData, ShipOrderUpdateRequest, ShipOrderUpdateRequestItem
} from '../apis/shipOrder.api'
import { ItemType } from '../apis/ItemType.api'
import dateUtil from '../utils/date.util'
import confirmService from '../services/confirm.service'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import { ActionName } from '../components/Actions'
import Card from '../components/Card'
import Row from '../components/Row/Row'
import PageHeading from '../components/PageHeading'
import InputText from '../components/InputText'
import Button from '../components/Button'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import DatePicker from '../components/DatePicker/DatePicker'
import CardButton from '../components/CardButton'
import TableHorizontal, {
  TableHorizontalHeader
} from '../components/TableHorizontal'

interface RouteState {
  id: number
}

export interface ShipOrderEditItem extends ShipOrderUpdateRequestItem {
  actions: ActionName[]
  code: string
  description: string
  qpc: number
}

interface Props extends RouteComponentProps<any, any, RouteState> {
  onItemChange: (item: ItemData | null) => void
  onQuantityChange: (quantity: number) => void
}

interface State {
  item: string
  quantity: string
  purchaseOrder: string
  salesOrder: string
  freightNumber: string
  shipAt: string
  deliveryAt: string
  note: string
  selectedItem: ItemData | null
  items: ItemData[]
  shipOrderItems: ShipOrderEditItem[]
  shipOrder: ShipOrderData | null
}

class ShipOrderEditForm extends Component<Props, State> {
    editActions:ActionName[] = ['edit', 'delete']
    tableHeaders: TableHorizontalHeader[] = [
      { heading: i18n("Item code"), path: 'code' },
      { heading: i18n("Description"), path: 'description' },
      { heading: i18n("Quantity"), path: 'quantity' },
      { heading: i18n("Actions") }
    ]

    constructor (props: Props) {
      super(props)
      const date = dateUtil.now()
      this.state = {
        item: '',
        quantity: '',
        purchaseOrder: '',
        salesOrder: '',
        freightNumber: '',
        shipAt: date,
        deliveryAt: date,
        note: '',
        selectedItem: null,
        items: [],
        shipOrderItems: [],
        shipOrder: null
      }
      this.handleInputChange = this.handleInputChange.bind(this)
      this.handleAddClick = this.handleAddClick.bind(this)
      this.handleItemSearch = this.handleItemSearch.bind(this)
      this.handleActionClick = this.handleActionClick.bind(this)
      this.handleDatePickerChange = this.handleDatePickerChange.bind(this)
      this.handleSaveClick = this.handleSaveClick.bind(this)
      this.handleSearchSelect = this.handleSearchSelect.bind(this)
      this.handleItemBlur = this.handleItemBlur.bind(this)
    }

    get id () {
      return this.props.location.state.id
    }

    get itemSearchOptions () {
      return itemService.searchOptions(this.state.items)
    }

    get selectedItemId () {
      const { selectedItem } = this.state
      if (!selectedItem) return null
      return selectedItem.id
    }

    get shipOrderItems ():ShipOrderUpdateRequestItem[] {
      return map(this.state.shipOrderItems, shipOrderItem => {
        const { id, quantity } = shipOrderItem
        return { id, quantity }
      })
    }

    get deletedItems ():number[] {
      const { shipOrder, shipOrderItems } = this.state
      if (!shipOrder || !shipOrder.items) return []
      const items = shipOrder.items
      const deletedItems = map(items, item => {
        const shipOrderItem = find(shipOrderItems, { id: item.id })
        if (
          !shipOrderItem || !item.shipOrderItem ||
          !item.category
        ) return item.id
        const hasChanged = (
          shipOrderItem.quantity !== item.shipOrderItem?.quantity
        )
        if (hasChanged) return item.id
      })
      return compact(deletedItems)
    }

    save () {
      const shipOrderItems = this.shipOrderItems
      const itemsDeleted = this.deletedItems
      const { purchaseOrder } = this.state
      const { salesOrder, freightNumber, shipAt, deliveryAt } = this.state
      const { note } = this.state
      const payload: ShipOrderUpdateRequest = {}
      if (shipOrderItems) payload.items = shipOrderItems
      if (itemsDeleted) payload.itemsDeleted = itemsDeleted
      if (purchaseOrder) payload.purchaseOrder = purchaseOrder
      if (salesOrder) payload.salesOrder = salesOrder
      if (freightNumber) payload.freightNumber = freightNumber
      if (shipAt) payload.shipDatetime = shipAt
      if (deliveryAt) payload.deliveryDate = deliveryAt
      if (note) payload.note = note
      shipOrderApi.update(this.id, payload, shipOrder => {
        if (!shipOrder) return
        this.props.history.push(routes.shipOrders)
      })
    }

    handleInputChange (name: string, value: string) {
      if (name === 'item') {
        this.setState({ selectedItem: null, item: value })
        this.props.onItemChange(null)
      } else if (name === 'quantity') {
        this.setState({ quantity: value })
        this.props.onQuantityChange(Number(value) || 0)
      } else if (name === 'purchaseOrder') {
        this.setState({ purchaseOrder: value })
      } else if (name === 'salesOrder') this.setState({ salesOrder: value })
      else if (name === 'freightNumber') this.setState({ freightNumber: value })
      else if (name === 'note') this.setState({ note: value })
    }

    handleAddClick () {
      const { selectedItem, quantity } = this.state
      if (!selectedItem || !quantity) return
      const shipOrderItem: ShipOrderEditItem = {
        actions: this.editActions,
        id: selectedItem.id,
        code: selectedItem.code,
        description: selectedItem.description,
        qpc: selectedItem.qpc,
        quantity: Number(quantity)
      }
      this.setState(state => {
        return produce(state, draft => {
          draft.shipOrderItems = reject(draft.shipOrderItems, { id: selectedItem.id })
          draft.shipOrderItems.unshift(shipOrderItem)
          draft.item = ''
          draft.quantity = ''
          draft.selectedItem = null
        })
      })
      this.props.onItemChange(null)
      this.props.onQuantityChange(0)
    }

    handleItemSearch (name: string, term: string) {
      const types: ItemType[] = ['production', 'receivable']
      itemApi.searchByCode(term, types, items => {
        if (!items) return
        this.setState({ items })
      })
    }

    handleSearchSelect (name: string, option: SearchPickerOption) {
      if (name === 'item') {
        const { value } = option
        const itemId = option.id as number
        const selectedItem = itemService.find(this.state.items, itemId)
        if (!selectedItem) return
        this.props.onItemChange(selectedItem)
        this.setState({ items: [], item: value, selectedItem })
      }
    }

    handleItemBlur (name: string) {
      this.setState({ items: [] })
    }

    handleActionClick (id: number, action: ActionName) {
      if (action === 'delete') {
        const confirm: ConfirmParams = {
          id,
          type: 'warning',
          question: i18n('Are you sure to delete the ship order item?'),
          confirmText: i18n('Yes'),
          cancelText: i18n('No'),
          onConfirm: () => this.handleDeleteConfirm(id)
        }
        confirmService.push(confirm)
      } else if (action === 'edit') {
        const shipOrderItem = find(this.state.shipOrderItems, { id })
        if (!shipOrderItem) return
        itemApi.read(Number(id), item => {
          if (!item) return
          this.setState({
            item: shipOrderItem.code,
            quantity: String(shipOrderItem.quantity),
            selectedItem: item
          })
          this.props.onItemChange(item)
          this.props.onQuantityChange(shipOrderItem.quantity)
        })
      }
    }

    handleDeleteConfirm (id: number) {
      this.setState(state => {
        return produce(state, draft => {
          const index = findIndex(draft.shipOrderItems, { id })
          pullAt(draft.shipOrderItems, index)
        })
      })
    }

    handleDatePickerChange (name: string, date: string) {
      if (name === 'shipAt') this.setState({ shipAt: date })
      if (name === 'deliveryAt') this.setState({ deliveryAt: date })
    }

    handleSaveClick () {
      this.save()
    }

    readShipOrder () {
      shipOrderApi.read(this.id, shipOrder => {
        if (!shipOrder) return
        const { purchaseOrder, salesOrder, freightNumber } = shipOrder
        const { shipAt, deliveryAt, note, items } = shipOrder
        this.setState({
          purchaseOrder: purchaseOrder || '',
          salesOrder: salesOrder || '',
          freightNumber: freightNumber || ''
        })
        this.setState({ shipAt, deliveryAt, note, shipOrder })
        if (!items) return
        const shipOrderItems: ShipOrderEditItem[] = compact(map(items, item => {
          if (!item.shipOrderItem || !item.category) return null
          const actions = this.editActions
          const { id, code, description, qpc } = item
          const quantity = item.shipOrderItem.quantity
          return { actions, id, code, description, qpc, quantity }
        }))
        this.setState({ shipOrderItems })
      })
    }

    componentDidMount () {
      this.readShipOrder()
    }

    render () {
      return (
        <Card>
          <PageHeading
            text={i18n("Edit Ship Order")}
            doc='ship-orders-edit-ship-order' />
          <Row column={3}>
            <SearchPicker
              name="item"
              value={this.state.item}
              options={this.itemSearchOptions}
              label={i18n("Item")}
              onSearch={this.handleItemSearch}
              onChange={this.handleInputChange}
              onSelect={this.handleSearchSelect}
              onBlur={this.handleItemBlur} />
            <InputText
              name="quantity"
              value={this.state.quantity}
              label={i18n("Quantity")}
              onChange={this.handleInputChange} />
            <div className="flex items-end mb-4">
              <Button
                text={i18n("Add")}
                full={true}
                onClick={this.handleAddClick} />
            </div>
          </Row>
          <Row column={1}>
            <TableHorizontal
              headers={this.tableHeaders}
              data={this.state.shipOrderItems}
              onActionClick={this.handleActionClick} />
          </Row>
          <Row column={2}>
            <InputText
              name="purchaseOrder"
              value={this.state.purchaseOrder}
              label={i18n("Purchase order")}
              onChange={this.handleInputChange} />
            <InputText
              name="salesOrder"
              value={this.state.salesOrder}
              label={i18n("Sales order")}
              onChange={this.handleInputChange} />
            <InputText
              name="freightNumber"
              value={this.state.freightNumber}
              label={i18n("Freight number")}
              onChange={this.handleInputChange} />
            <DatePicker
              name="shipAt"
              value={this.state.shipAt}
              label={i18n("Ship datetime")}
              time={true}
              onChange={this.handleDatePickerChange} />
          </Row>
          <Row column={2}>
            <DatePicker
              name="deliveryAt"
              value={this.state.deliveryAt}
              label={i18n("Delivery date")}
              onChange={this.handleDatePickerChange} />
            <InputText
              name="note"
              value={this.state.note}
              label={i18n("Note")}
              onChange={this.handleInputChange} />
          </Row>
          <CardButton
            proceedText={i18n("Save")}
            cancelText={i18n("Cancel")}
            onProceedClick={this.handleSaveClick} />
        </Card>
      )
    }
}

export default withRouter(ShipOrderEditForm)
